import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Link, Stack, TextField, Typography } from '@mui/material';
import { authApi } from '@/auth/authApi';
import { paths } from '@/routes/paths';
import { applyServerErrors } from '@/utils/formErrors';
import { forgotPasswordSchema, type ForgotPasswordValues } from '@/features/auth/schemas';

export function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);
  const [formError, setFormError] = useState('');
  const form = useForm<ForgotPasswordValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  });

  const mutation = useMutation({
    mutationFn: (values: ForgotPasswordValues) => authApi.forgotPassword(values),
    onSuccess: () => setSent(true),
    onError: (error) => setFormError(applyServerErrors(error, form.setError, ['email'])),
  });

  if (sent) {
    return (
      <Stack spacing={2}>
        <Typography variant="h6">Check your email</Typography>
        <Alert severity="success">
          If an account exists for that address, we&apos;ve sent a password reset link.
        </Alert>
        <Link component={RouterLink} to={paths.login} variant="body2" underline="hover">
          Back to sign in
        </Link>
      </Stack>
    );
  }

  return (
    <Stack spacing={2.5} component="form" onSubmit={form.handleSubmit((v) => mutation.mutate(v))}>
      <Box>
        <Typography variant="h6">Reset your password</Typography>
        <Typography variant="body2" color="text.secondary">
          Enter your email and we&apos;ll send a reset link.
        </Typography>
      </Box>
      {formError && <Alert severity="error">{formError}</Alert>}
      <TextField
        label="Email"
        type="email"
        autoComplete="email"
        autoFocus
        error={!!form.formState.errors.email}
        helperText={form.formState.errors.email?.message}
        {...form.register('email')}
      />
      <Button type="submit" variant="contained" size="large" disabled={mutation.isPending}>
        {mutation.isPending ? 'Sending…' : 'Send reset link'}
      </Button>
      <Link component={RouterLink} to={paths.login} variant="body2" underline="hover">
        Back to sign in
      </Link>
    </Stack>
  );
}
